// ═══════════════════════════════════════════════════════════════
// Shared CLIN Description Lookup
// Fetches rate schedule descriptions once and caches globally.
// Usage: const { clinLabel } = useClinDescriptions();
//        clinLabel('1AN') => '1AN — AMD1 Vegetative Debris to DMS'
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { queryRecords } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';

let _cache: Record<string, string> | null = null;
let _loading = false;
const _listeners = new Set<() => void>();

export function useClinDescriptions(): {
  clinDesc: (code: string) => string;
  clinLabel: (code: string) => string;
  loaded: boolean;
} {
  const [loaded, setLoaded] = useState(!!_cache);

  useEffect(() => {
    if (_cache) { setLoaded(true); return; }
    const listener = () => setLoaded(true);
    _listeners.add(listener);

    if (!_loading) {
      _loading = true;
      (async () => {
        try {
          const FR = FIELDS.debrisRateSchedules;
          const res = await queryRecords<Record<string, unknown>>(TABLES.debrisRateSchedules, { limit: 500 });
          const map: Record<string, string> = {};
          res.records.forEach((r: any) => {
            const code = String(r[FR.lineItemCode] || '').trim();
            const desc = String(r[FR.description] || '').trim();
            if (code && desc) map[code] = desc;
          });
          _cache = map;
        } catch { _cache = {}; }
        _listeners.forEach(l => l());
        _listeners.clear();
      })();
    }

    return () => { _listeners.delete(listener); };
  }, []);

  const clinDesc = (code: string): string => {
    if (!code) return '';
    return _cache?.[code] || '';
  };

  const clinLabel = (code: string): string => {
    if (!code) return '-';
    const desc = _cache?.[code];
    return desc ? `${code} — ${desc}` : code;
  };

  return { clinDesc, clinLabel, loaded };
}
